import React, { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PanelHeader from '../../components/PanelHeader';
import ShareScopeSelect from '../../components/ShareScopeSelect';
import { C, FONT, capsuleSx, frameSx, glow, hexToRgba } from '../../theme/tokens';
import { MapFolder, ShareScope } from '../../types';
import { unsealSx, SEAL_LABEL } from './vaultMotion';

/** The seal is the vault metaphor's load-bearing detail: who this vault is
 * sealed to, stated as a value rather than buried in a settings dialog. */
/**
 * The rail of vaults — a single wrapping row of seal chips rather than a
 * dedicated vertical column. A corp keeps a handful of vaults at most, so a
 * tall narrow rail either sat mostly empty below the list (most corps) or
 * pushed the actual working area (compartments, diagram, tracked feed) into
 * a cramped remainder (a corp with many vaults) — wrong trade either way. A
 * row costs exactly the height its own content needs and never more, and it
 * hands the full page width back to whatever is actually open.
 *
 * Each entry is still a sealed door, just laid flat: locked and dim until it
 * is the open one, which takes the wormhole glow and an open lock. Deleting
 * one is destructive enough (every entry and connection in it goes with it)
 * to warrant asking once — see the confirm below — unlike removing a single
 * entry or connection, which stays instant.
 */
const VaultRail: React.FC<{
  vaults: MapFolder[];
  activeId: number | null;
  ownCharacterId: number | undefined;
  isAdmin: boolean;
  /** A read-only grant still picks vaults; it just cannot make or drop one. */
  canEdit: boolean;
  onSelect: (id: number) => void;
  onCreate: (v: { name: string; scope: ShareScope; groupId: number | null }) => void;
  onDelete: (id: number) => void;
  creating: boolean;
  createError: boolean;
}> = ({ vaults, activeId, ownCharacterId, isAdmin, canEdit, onSelect, onCreate, onDelete, creating, createError }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<ShareScope>('corp');
  const [groupId, setGroupId] = useState<number | null>(null);

  return (
    <Box>
      <PanelHeader label="Vaults" accent={C.cyan} dense sx={{ mb: 1 }} />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
        {vaults.map((vault, i) => (
          <VaultChip
            key={vault.id}
            vault={vault}
            active={activeId === vault.id}
            // The creator can always delete their own; an admin can delete
            // any vault (see requireMapOwnerOrAdmin server-side) — every
            // deletion this override performs is written to
            // vault_deletion_log, never silent.
            deletable={canEdit && (vault.owner_character_id === ownCharacterId || isAdmin)}
            index={i}
            onSelect={() => onSelect(vault.id)}
            onDelete={() => onDelete(vault.id)}
          />
        ))}

        {/* Creating a vault is a write. A read-only grant gets the rail
            without it rather than a button that 403s. */}
        {!canEdit ? null : !open ? (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(true);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: 999,
              border: `1px dashed ${C.hairlineBright}`,
              color: C.textDim,
              cursor: 'pointer',
              fontFamily: FONT.sub,
              fontWeight: 600,
              fontSize: '0.85rem',
              transition: 'border-color 0.2s ease, color 0.2s ease',
              '&:hover': { borderColor: C.cyan, color: C.cyan },
              '&:focus-visible': { outline: `1px solid ${C.cyan}`, outlineOffset: 2 },
            }}
          >
            + New Vault
          </Box>
        ) : (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ ...frameSx(C.wormhole), p: 1, rowGap: 1, animation: 'none' }}
          >
            <TextField
              size="small"
              autoFocus
              placeholder="Vault name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) onCreate({ name, scope, groupId });
              }}
              sx={{ width: 180 }}
            />
            <ShareScopeSelect
              value={scope}
              groupId={groupId}
              onChange={(s, g) => {
                setScope(s);
                setGroupId(g);
              }}
            />
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={!name.trim() || creating}
              onClick={() => onCreate({ name, scope, groupId })}
            >
              {creating ? 'Sealing…' : 'Create'}
            </Button>
            <Button size="small" onClick={() => setOpen(false)} sx={{ color: C.textDim }}>
              Cancel
            </Button>
            {createError && (
              <Typography sx={{ color: C.red, fontSize: '0.72rem', width: '100%' }}>
                Couldn&rsquo;t create vault — try again.
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const VaultChip: React.FC<{
  vault: MapFolder;
  active: boolean;
  deletable: boolean;
  index: number;
  onSelect: () => void;
  onDelete: () => void;
}> = ({ vault, active, deletable, index, onSelect, onDelete }) => {
  const scopeKey = (vault.share_scope ?? 'private') as ShareScope;

  return (
    <Stack
      direction="row"
      alignItems="center"
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={active}
      sx={{
        ...capsuleSx(active ? C.wormhole : C.cyan, active),
        ...unsealSx(index),
        pl: 1.75,
        pr: deletable ? 0.75 : 1.75,
        py: 0.75,
        gap: 1,
        cursor: 'pointer',
        boxShadow: active ? glow(C.wormhole, 20, 0.28) : undefined,
        borderColor: active ? hexToRgba(C.wormhole, 0.55) : undefined,
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
        // The unlink control only ever shows once this chip already has
        // room for it (`deletable`), so there is no layout shift when it
        // fades in on hover — it occupies its slot from the first paint.
        '&:hover': { borderColor: hexToRgba(active ? C.wormhole : C.cyan, 0.45) },
        '&:hover .vault-delete': { opacity: 1 },
        '&:focus-visible': { outline: `1px solid ${C.cyan}`, outlineOffset: 2 },
      }}
    >
      {active ? <LockOpenIcon sx={{ fontSize: 15, color: C.wormhole, flexShrink: 0 }} /> : <LockIcon sx={{ fontSize: 15, color: C.textDim, flexShrink: 0 }} />}
      <Typography
        noWrap
        sx={{ fontFamily: FONT.sub, fontWeight: 700, fontSize: '0.85rem', color: active ? C.textBright : C.text }}
      >
        {vault.name}
      </Typography>
      <Typography sx={{ fontSize: '0.6rem', fontFamily: FONT.mono, color: C.textDim, letterSpacing: '0.08em', flexShrink: 0 }}>
        {SEAL_LABEL[scopeKey] ?? scopeKey}
      </Typography>
      {deletable && (
        <Box
          className="vault-delete"
          role="button"
          aria-label={`Delete vault ${vault.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            color: C.textDim,
            opacity: 0,
            transition: 'opacity 0.15s ease, color 0.15s ease, background-color 0.15s ease',
            '&:hover': { color: C.red, backgroundColor: hexToRgba(C.red, 0.12) },
          }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </Box>
      )}
    </Stack>
  );
};

export default VaultRail;
